/**
 * The industry taxonomy members choose from at signup, and the icons the member
 * directory browses them by.
 *
 * Shared rather than page-local because the directory has to list every category
 * — including the ones nobody has joined yet — so a browsable list cannot be
 * derived from the members who happen to be in it.
 */

/**
 * The escape hatch, kept out of the alphabetical run because it is not an
 * industry. Selecting it reveals a free-text field, and what gets stored is
 * whatever the member typed — never the word "Other", which would tell you
 * nothing when you read the directory later.
 */
export const OTHER_INDUSTRY = "Other";

export const INDUSTRIES = [
  "Accounting & Finance",
  "Advertising & Marketing",
  "Aerospace & Defense",
  "Agriculture & Food Production",
  "Architecture & Construction",
  "Automotive",
  "Banking & Financial Services",
  "Biotechnology & Life Sciences",
  "Consulting & Professional Services",
  "Consumer Goods & Retail",
  "Cybersecurity",
  "E-Commerce",
  "Education & EdTech",
  "Energy & Utilities",
  "Entertainment & Media",
  "Environmental & Sustainability",
  "Fashion & Apparel",
  "Government & Public Sector",
  "Healthcare & MedTech",
  "Hospitality & Tourism",
  "Human Resources & People Ops",
  "Insurance",
  "Legal Services",
  "Logistics & Supply Chain",
  "Manufacturing",
  "Nonprofit & Social Impact",
  "Pharmaceuticals",
  "Private Equity & Venture Capital",
  "Real Estate",
  "Recruiting & Talent Acquisition",
  "SaaS & Enterprise Software",
  "Startup & Entrepreneurship",
  "Technology & IT Services",
  "Telecommunications",
  "Transportation",
  OTHER_INDUSTRY,
];

/**
 * One glyph per category, so a long list can be scanned by shape before it is
 * read. Decorative: every row is labelled in text, and the icons are hidden
 * from assistive technology.
 */
export const INDUSTRY_ICONS: Record<string, string> = {
  "Accounting & Finance": "💰",
  "Advertising & Marketing": "📣",
  "Aerospace & Defense": "✈️",
  "Agriculture & Food Production": "🚜",
  "Architecture & Construction": "🏛️",
  "Automotive": "🚗",
  "Banking & Financial Services": "🏦",
  "Biotechnology & Life Sciences": "🧬",
  "Consulting & Professional Services": "💼",
  "Consumer Goods & Retail": "🛍️",
  "Cybersecurity": "🛡️",
  "E-Commerce": "🛒",
  "Education & EdTech": "🎓",
  "Energy & Utilities": "⚡",
  "Entertainment & Media": "🎬",
  "Environmental & Sustainability": "🌱",
  "Fashion & Apparel": "👗",
  "Government & Public Sector": "🏛",
  "Healthcare & MedTech": "🩺",
  "Hospitality & Tourism": "🏨",
  "Human Resources & People Ops": "🤝",
  "Insurance": "🛟",
  "Legal Services": "⚖️",
  "Logistics & Supply Chain": "📦",
  "Manufacturing": "🏭",
  "Nonprofit & Social Impact": "🕊️",
  "Pharmaceuticals": "💊",
  "Private Equity & Venture Capital": "📈",
  "Real Estate": "🏠",
  "Recruiting & Talent Acquisition": "🧭",
  "SaaS & Enterprise Software": "🧩",
  "Startup & Entrepreneurship": "🚀",
  "Technology & IT Services": "💻",
  "Telecommunications": "📡",
  "Transportation": "🚚",
  [OTHER_INDUSTRY]: "✳️",
};

/** Falls back to a neutral glyph for industries typed into the "Other" field. */
export function industryIcon(industry: string): string {
  return INDUSTRY_ICONS[industry] ?? "✳️";
}
